/**
 * FAQ page: reveals the hero search, filters the questions as the
 * field is typed in, and marks the group being read in the side nav.
 *
 * Everything here is enhancement. The section renders complete and
 * usable without it: the field is `hidden` in the HTML and only this
 * module shows it.
 */
class FaqPage extends HTMLElement {
  connectedCallback() {
    this.field = this.querySelector('[data-faq-field]');
    this.status = this.querySelector('[data-faq-status]');
    this.empty = this.querySelector('[data-faq-empty]');
    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
    this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
    this.shortcuts = Array.from(this.querySelectorAll('[data-faq-shortcut]'));

    // Indexed once: question and answer are both in textContent
    // whether the item is open or closed, so a closed answer is
    // searchable too.
    this.index = this.items.map((item) => normalise(item.textContent));

    const search = this.querySelector('.faq-page__search');
    if (search && this.field) {
      search.removeAttribute('hidden');
      this.field.addEventListener('input', () => this.filter());
      this.field.addEventListener('search', () => this.filter());

      const submit = this.querySelector('[data-faq-submit]');
      if (submit) {
        submit.addEventListener('click', () => {
          this.filter();
          this.field.focus();
        });
      }
    }

    this.watchGroups();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  filter() {
    const query = normalise(this.field.value);
    let matches = 0;

    this.items.forEach((item, i) => {
      const hit = query === '' || this.index[i].includes(query);
      item.toggleAttribute('hidden', !hit);
      if (hit) matches += 1;
    });

    // A group with no visible question has nothing left to show, and
    // neither do its chip and its nav entry.
    for (const group of this.groups) {
      const alive = group.querySelector('[data-faq-item]:not([hidden])') !== null;
      group.toggleAttribute('hidden', !alive);

      const anchor = group.getAttribute('data-faq-group');
      for (const shortcut of this.shortcuts) {
        if (shortcut.getAttribute('data-faq-shortcut') !== anchor) continue;
        const row = shortcut.closest('li') || shortcut;
        row.toggleAttribute('hidden', !alive);
      }
    }

    if (this.empty) this.empty.toggleAttribute('hidden', query === '' || matches > 0);
    if (this.status) this.status.textContent = query === '' ? '' : this.count(matches);
  }

  /** The plural form comes from the markup; only the number is code. */
  count(matches) {
    const data = this.status.dataset;
    if (matches === 1) return data.resultsOne;
    return data.resultsOther.replace('%count%', matches);
  }

  /**
   * Marks the group being read. The top margin of the root is the
   * anchor offset, the same value scroll-margin uses, so a group only
   * counts as read once it has cleared the sticky header.
   */
  watchGroups() {
    if (this.groups.length === 0 || !('IntersectionObserver' in window)) return;

    const offset = parseInt(getComputedStyle(this).getPropertyValue('--faq-page-anchor-offset'), 10) || 0;
    const visible = new Set();

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }

        // Nothing in the band means the page is above the first group
        // or between two of them: the nav keeps the last topic read, and
        // starts on the first, the way the Figma draws it.
        const current = this.groups.find((group) => visible.has(group)) || this.current || this.groups[0];
        this.current = current;
        const anchor = current.getAttribute('data-faq-group');

        for (const shortcut of this.shortcuts) {
          if (anchor !== null && shortcut.getAttribute('data-faq-shortcut') === anchor) {
            shortcut.setAttribute('aria-current', 'true');
          } else {
            shortcut.removeAttribute('aria-current');
          }
        }
      },
      { rootMargin: '-' + offset + 'px 0px -60% 0px' }
    );

    for (const group of this.groups) this.observer.observe(group);
  }
}

/** Lower case with the accents stripped: "gravacao" finds "Gravação". */
function normalise(value) {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

if (!customElements.get('faq-page')) customElements.define('faq-page', FaqPage);
